/**
 * RefillGoodsPhase - 商品補充フェーズUIコンポーネント
 *
 * FRONTEND.md Step.3 準拠:
 * - Lovable スタイル適用済み
 *
 * @see FRONTEND.md
 * @see src/types/ui-interfaces.ts
 */

import type { RefillGoodsPhaseProps } from '../../types/ui-interfaces';
import type { ReactNode } from 'react';
import { useTranslations } from '../../i18n';

/**
 * RefillGoodsPhaseの拡張Props
 */
interface RefillGoodsPhaseExtendedProps extends RefillGoodsPhaseProps {
  /** 自動処理フェーズ用コンテンツ（Production未選択時） */
  autoPhaseContent?: ReactNode;
}

/**
 * Phase 9: 商品補充フェーズUI（共通）
 *
 * 機能:
 * - Productionアクション選択時: 商品ディスプレイボードで手動配置
 * - 通常時: 自動処理（ダイスロール）の表示
 */
export function RefillGoodsPhase({
  isProductionSelected,
  drawnGoods,
  productionPlacementCount: _productionPlacementCount,
  showGoodsDisplay,
  onShowGoodsDisplay,
  autoPhaseContent,
}: RefillGoodsPhaseExtendedProps) {
  const t = useTranslations();

  // Productionアクション選択時の手動UI
  if (isProductionSelected) {
    return (
      <div className="space-y-4">
        {/* 説明 */}
        <div className="message-info">
          <div className="font-medium text-sm">{t.refillGoods.productionSelected}</div>
        </div>

        {/* 引いた商品の表示 */}
        {drawnGoods && (
          <div className="p-3 bg-muted/50 rounded">
            <div className="text-sm font-medium mb-2">{t.refillGoods.drawnGoods}</div>
            <div className="flex gap-2">
              {drawnGoods.map((color, index) => (
                <span key={index} className={`block w-6 h-6 rounded-sm bg-goods-${color}`} />
              ))}
            </div>
          </div>
        )}

        {/* 商品ディスプレイボード表示ボタン */}
        {!showGoodsDisplay && (
          <button
            className="btn-primary w-full"
            onClick={onShowGoodsDisplay}
          >
            {t.refillGoods.openGoodsDisplay}
          </button>
        )}

        {/* GoodsDisplayBoard は親コンポーネントでオーバーレイとして表示 */}
      </div>
    );
  }

  // 通常時（Productionアクション未選択）: 自動処理コンテンツを表示
  return (
    <div className="space-y-4">
      {autoPhaseContent || (
        <p className="text-muted-foreground text-sm">Rolling dice...</p>
      )}
    </div>
  );
}

export default RefillGoodsPhase;
